
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Calendar, 
  Send, 
  Download,
  Settings,
  Clock,
  CheckCircle
} from "lucide-react";
import { format } from "date-fns";

interface ClientReport {
  id: string;
  type: 'weekly' | 'monthly' | 'quarterly';
  title: string;
  generatedDate: Date;
  sentDate?: Date;
  status: 'draft' | 'sent' | 'scheduled';
  recipients: string[];
  metrics: {
    candidatesSubmitted: number;
    interviewsScheduled: number;
    offersExtended: number;
    placements: number;
  };
}

interface ClientReportingTabProps {
  clientId: string;
  clientName: string;
}

// Mock data for reports
const mockReports: ClientReport[] = [
  {
    id: '1',
    type: 'weekly',
    title: 'Weekly Progress Report - Week of Jan 15',
    generatedDate: new Date('2024-01-19T09:00:00'),
    sentDate: new Date('2024-01-19T10:30:00'),
    status: 'sent',
    recipients: ['client@company.com', 'hr@company.com'],
    metrics: {
      candidatesSubmitted: 5,
      interviewsScheduled: 3,
      offersExtended: 1,
      placements: 0
    }
  },
  {
    id: '2',
    type: 'weekly',
    title: 'Weekly Progress Report - Week of Jan 22',
    generatedDate: new Date('2024-01-26T09:00:00'),
    status: 'draft',
    recipients: ['client@company.com', 'hr@company.com'],
    metrics: {
      candidatesSubmitted: 8,
      interviewsScheduled: 4,
      offersExtended: 2,
      placements: 1
    }
  }
];

export const ClientReportingTab: React.FC<ClientReportingTabProps> = ({ 
  clientId, 
  clientName 
}) => {
  const [reports] = useState<ClientReport[]>(mockReports);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent': return <CheckCircle className="h-4 w-4" />;
      case 'draft': return <FileText className="h-4 w-4" />;
      case 'scheduled': return <Clock className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Report Generation Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Report Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="outline" className="h-20 flex flex-col items-center gap-2">
              <Calendar className="h-6 w-6" />
              <span className="text-sm">Generate Weekly Report</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center gap-2">
              <Calendar className="h-6 w-6" />
              <span className="text-sm">Generate Monthly Report</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center gap-2">
              <Calendar className="h-6 w-6" />
              <span className="text-sm">Generate Quarterly Report</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reports List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generated Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {reports.map((report) => (
              <div key={report.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium">{report.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      Generated on {format(report.generatedDate, 'MMM d, yyyy h:mm a')}
                    </p>
                    {report.sentDate && (
                      <p className="text-sm text-muted-foreground">
                        Sent on {format(report.sentDate, 'MMM d, yyyy h:mm a')}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      Recipients: {report.recipients.join(', ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(report.status)}>
                      {getStatusIcon(report.status)}
                      <span className="ml-1 capitalize">{report.status}</span>
                    </Badge>
                  </div>
                </div>

                {/* Report Metrics Preview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-gray-50 rounded">
                  <div className="text-center">
                    <div className="text-lg font-semibold">{report.metrics.candidatesSubmitted}</div>
                    <div className="text-xs text-muted-foreground">Candidates Submitted</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold">{report.metrics.interviewsScheduled}</div>
                    <div className="text-xs text-muted-foreground">Interviews Scheduled</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold">{report.metrics.offersExtended}</div>
                    <div className="text-xs text-muted-foreground">Offers Extended</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-green-600">{report.metrics.placements}</div>
                    <div className="text-xs text-muted-foreground">Placements</div>
                  </div>
                </div>

                {/* Report Actions */}
                <div className="flex items-center gap-2 pt-2">
                  <Button size="sm" variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Download PDF
                  </Button>
                  {report.status === 'draft' && (
                    <Button size="sm">
                      <Send className="mr-2 h-4 w-4" />
                      Send Report
                    </Button>
                  )}
                  <Button size="sm" variant="outline">
                    <FileText className="mr-2 h-4 w-4" />
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
